import {
  describeNonConformities,
  type QualityLine,
} from '@obraplan/core/domain/planning/daily-report-quality';
import { describeDailyReport, type DailyReport } from '@obraplan/core/domain/planning/daily-report';
import {
  EQUIPMENT_STATUS_LABEL,
  type CrewLine,
  type EquipmentLine,
} from '@obraplan/core/domain/planning/daily-report-crew';
import {
  describeTaskDetails,
  describeWorkers,
  type ExecutedFrontLine,
} from '@obraplan/core/domain/planning/daily-report-task';
import {
  reportedShifts,
  REPORT_SHIFT_LABEL,
  WEATHER_CONDITION_LABEL,
  type ShiftWeather,
} from '@obraplan/core/domain/planning/daily-report-weather';
import { projectTerm } from '@obraplan/core/domain/planning/daily-report-term';
import type { Project } from '@obraplan/core/domain/structure/project';
import { LOGO_DATA_URL, LOGO_WIDTH } from './report-logo';

/**
 * O RDO em papel (Fase 18, fatia 7).
 *
 * Duas páginas: o documento e o relatório fotográfico. O que está aqui é **só a montagem**
 * — uma função pura que recebe o dia já lido e devolve a definição do documento. Quem
 * carrega o `pdfmake` e baixa o arquivo é `emitirPdfDoRelatorio`, embaixo, e a separação é
 * o que permite testar o papel inteiro sem navegador e sem biblioteca.
 *
 * ## Por que é gerado, e não impresso (ADR-124)
 *
 * Os três impressos de hoje saem por `window.print()`, e para eles isso basta: são folhas
 * de trabalho, impressas na hora. O RDO é anexado em e-mail, arquivado com nome
 * padronizado e numerado — passar pela caixa de impressão do navegador para chegar a um
 * arquivo é pedir que alguém acerte a margem certa todo dia.
 *
 * ## O que não entra no papel
 *
 * - **o turno em branco** (RN-211): no RDO de referência a noite está vazia, e imprimir
 *   "Noite: — —" diria que alguém olhou e não encontrou nada, quando a verdade é que não
 *   se trabalha à noite naquela obra;
 * - **a coluna "contado" do efetivo** (RN-212): vale o corrigido, e é ele que vai ao
 *   papel. O contado é conferência interna, e fica na tela;
 * - **a quantidade da frente que ninguém mediu**: célula vazia, e não `0,00`. Zero é a
 *   afirmação de que não se produziu nada, e ela precisa de alguém que a tenha feito;
 * - **a segunda página**, quando não há foto nenhuma. Uma página em branco com o título
 *   "Relatório fotográfico" é pior do que uma página a menos.
 */

// ── A definição do documento, só na medida em que este arquivo a usa ───────────────

interface TextNode {
  readonly text: string;
  readonly style?: string;
  readonly bold?: boolean;
  readonly italics?: boolean;
  readonly alignment?: 'left' | 'right' | 'center';
  readonly fontSize?: number;
  readonly margin?: readonly [number, number, number, number];
  readonly colSpan?: number;
  readonly rowSpan?: number;
  /** O cinza das barras de seção e dos rótulos do cabeçalho, como no RDO de referência. */
  readonly fillColor?: string;
}

type Cell = TextNode | StackNode | string;

interface TableNode {
  readonly table: {
    readonly headerRows: number;
    readonly widths: readonly (string | number)[];
    readonly body: readonly (readonly Cell[])[];
  };
  readonly layout?: string;
  readonly margin?: readonly [number, number, number, number];
}

interface ImageNode {
  readonly image: string;
  readonly width: number;
  readonly alignment?: 'left' | 'right' | 'center';
  readonly margin?: readonly [number, number, number, number];
}

interface StackNode {
  readonly stack: readonly (TextNode | ImageNode)[];
  readonly width?: string | number;
  readonly alignment?: 'left' | 'right' | 'center';
  readonly colSpan?: number;
  readonly margin?: readonly [number, number, number, number];
}

interface ColumnsNode {
  readonly columns: readonly StackNode[];
  readonly columnGap?: number;
  readonly margin?: readonly [number, number, number, number];
}

interface PageBreakNode {
  readonly text: string;
  readonly pageBreak: 'before';
  readonly style?: string;
}

export type DocNode = TextNode | TableNode | ColumnsNode | PageBreakNode | ImageNode;

export interface DocDefinition {
  readonly pageSize: 'A4';
  readonly pageMargins: readonly [number, number, number, number];
  readonly info: { readonly title: string };
  readonly defaultStyle: { readonly fontSize: number };
  readonly styles: Readonly<Record<string, Record<string, unknown>>>;
  readonly header: (pagina: number) => DocNode | undefined;
  readonly footer: (pagina: number, total: number) => DocNode;
  readonly content: readonly DocNode[];
}

/** A foto já resolvida em `data:` — o binário é lido pela tela, não por aqui. */
export interface ReportPhoto {
  readonly dataUrl: string;
  readonly caption: string;
}

/**
 * Os formatos que o `pdfmake` sabe embutir: JPEG e PNG, e nada mais.
 *
 * A galeria do RDO aceita também WebP e PDF (`ATTACHMENT_ACCEPT`), e o WebP que a compressão
 * não encolheu chega ao servidor como WebP. Entregue ao `pdfmake`, qualquer um dos dois
 * derruba o documento **inteiro** com "Unknown image format" — e o RDO do dia não sai por
 * causa de uma foto. A tela converte o que consegue para JPEG antes; o que ainda assim não
 * for um destes dois fica fora do papel.
 */
export const FORMATOS_DE_FOTO_DO_PAPEL: ReadonlySet<string> = new Set(['image/jpeg', 'image/png']);

function fotoCabeNoPapel(foto: ReportPhoto): boolean {
  const tipo = /^data:([^;,]+)/u.exec(foto.dataUrl)?.[1]?.toLowerCase();
  return tipo !== undefined && FORMATOS_DE_FOTO_DO_PAPEL.has(tipo);
}

export interface DailyReportDocumentInput {
  readonly project: Project;
  readonly report: DailyReport;
  /** `quinta-feira, 10/09/2026` — o mesmo rótulo que a tela mostra. */
  readonly dateLabel: string;
  readonly fronts: readonly ExecutedFrontLine[];
  /** A verificação de qualidade do dia (Fase 19). */
  readonly quality: readonly QualityLine[];
  readonly crew: readonly CrewLine[];
  readonly photos: readonly ReportPhoto[];
}

const VAZIO = '';

function dias(quantidade: number): string {
  return `${String(quantidade)} ${Math.abs(quantidade) === 1 ? 'dia' : 'dias'}`;
}

/**
 * O número medido, sem a unidade — ela é coluna própria no formulário.
 *
 * Célula vazia e `0,00` continuam sendo coisas diferentes: frente que ninguém mediu fica
 * em branco, porque zero é a afirmação de que não se produziu nada.
 */
function quantidadeDaFrente(linha: ExecutedFrontLine): string {
  if (linha.quantity === undefined) return VAZIO;
  const casas = linha.unitDecimals ?? 2;
  const formatar = (valor: number) =>
    valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
  // A parte não conforme sai da produção (Fase 37, RN-475): o papel diz as duas.
  return linha.nonConformingQuantity === undefined
    ? formatar(linha.quantity)
    : `${formatar(linha.quantity)}\n(${formatar(linha.nonConformingQuantity)} não conforme)`;
}

/** `2026-09-10` vira `10/09/2026` — a data do papel é a que se lê no canteiro. */
function dataBr(iso: string | undefined): string {
  if (iso === undefined) return VAZIO;
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return dia === undefined || mes === undefined || ano === undefined
    ? VAZIO
    : `${dia}/${mes}/${ano}`;
}

/** `quinta-feira, 10/09/2026` vira `['quinta-feira', '10/09/2026']`. */
function diaDaSemanaEData(rotulo: string): readonly [string, string] {
  const virgula = rotulo.indexOf(', ');
  return virgula < 0 ? [VAZIO, rotulo] : [rotulo.slice(0, virgula), rotulo.slice(virgula + 2)];
}

function horario(turno: ShiftWeather): string {
  if (turno.startTime === undefined || turno.endTime === undefined) return VAZIO;
  return `${turno.startTime} a ${turno.endTime}`;
}

/** O cinza das barras e dos rótulos — o mesmo do formulário de referência. */
const CINZA = '#e9e9e9';

/**
 * A barra de seção: o título centralizado sobre fundo cinza, ocupando a tabela inteira.
 *
 * É a primeira linha da própria tabela, e não um parágrafo solto acima dela: no formulário
 * a barra e a grade são a mesma caixa, e separá-las faria o título flutuar sozinho no fim
 * de uma página com a tabela na seguinte.
 */
function barra(texto: string, colunas: number): readonly Cell[] {
  return [
    { text: texto, style: 'barra', colSpan: colunas, alignment: 'center' as const },
    ...Array.from({ length: colunas - 1 }, () => VAZIO),
  ];
}

function cabecalhoDaTabela(...colunas: string[]): readonly Cell[] {
  return colunas.map((texto) => ({ text: texto, style: 'th', fillColor: CINZA }));
}

/** Rótulo e valor do cabeçalho — o rótulo cinza à direita, como no formulário. */
function campo(rotulo: string, valor: string): readonly [Cell, Cell] {
  return [{ text: rotulo, style: 'rotulo', fillColor: CINZA }, { text: valor }];
}

/** A célula que continua a anterior — o `colSpan` do `pdfmake` exige o lugar vazio. */
function vazias(quantidade: number): readonly Cell[] {
  return Array.from({ length: quantidade }, () => VAZIO);
}

/**
 * A tabela que sai vazia diz que saiu vazia.
 *
 * Uma seção que some quando não tem linha deixa quem lê sem saber se ela foi omitida ou
 * se não havia o que dizer — e num documento contratual essas duas coisas não podem ser a
 * mesma. A exceção é a segunda página, que é outra história e está dita acima.
 */
function tabela(
  secao: string,
  colunas: readonly string[],
  larguras: readonly (string | number)[],
  linhas: readonly (readonly Cell[])[],
  vazio: string,
): TableNode {
  const corpo =
    linhas.length > 0
      ? linhas
      : [[{ text: vazio, italics: true, colSpan: colunas.length }, ...vazias(colunas.length - 1)]];

  return {
    table: {
      // A barra e o cabeçalho se repetem quando a tabela vira a página: quem recebe a
      // folha solta precisa saber de que seção ela é, e o que cada coluna diz.
      headerRows: 2,
      widths: larguras,
      body: [barra(secao, colunas.length), cabecalhoDaTabela(...colunas), ...corpo],
    },
    margin: [0, 0, 0, 10],
  };
}

export function buildDailyReportDocument(entrada: DailyReportDocumentInput): DocDefinition {
  const { project, report, dateLabel, fronts, quality, crew } = entrada;
  const photos = entrada.photos.filter(fotoCabeNoPapel);
  const identificacao = describeDailyReport(report);
  const prazo = projectTerm({
    startDate: project.startDate,
    endDate: project.endDate,
    date: report.date,
  });

  const [diaDaSemana, dataCurta] = diaDaSemanaEData(dateLabel);

  const conteudo: DocNode[] = [
    /*
     * A marca no topo, alinhada à direita como no formulário de referência.
     *
     * Fica **fora** da grade do cabeçalho de propósito: a grade é a caixa de campos que
     * quem confere lê linha a linha, e uma imagem dentro dela empurraria os rótulos para
     * fora do lugar em que a pessoa já sabe procurá-los.
     */
    { image: LOGO_DATA_URL, width: LOGO_WIDTH, alignment: 'right' as const, margin: [0, 0, 0, 6] },
    /*
     * O cabeçalho é o do formulário que a obra já usa: a faixa com o nome do documento e,
     * embaixo, a grade de campos em duas colunas de rótulo e valor. Ele é uma tabela — e
     * não um empilhado de parágrafos — porque no papel é uma caixa só, e é assim que quem
     * confere lê: rótulo cinza à esquerda do valor, sempre no mesmo lugar.
     */
    {
      table: {
        headerRows: 0,
        widths: ['auto', '*', 'auto', '*'],
        body: [
          [
            {
              text: 'Diário de Obra',
              style: 'documento',
              colSpan: 4,
              alignment: 'center' as const,
              fillColor: CINZA,
            },
            ...vazias(3),
          ],
          [...campo('Obra:', project.name), ...campo('Início da Obra:', dataBr(project.startDate))],
          [
            ...campo('Prazo da Obra:', prazo === undefined ? VAZIO : dias(prazo.totalDays)),
            ...campo('Término da Obra:', dataBr(project.endDate)),
          ],
          [
            ...campo('Tempo decorrido:', prazo === undefined ? VAZIO : dias(prazo.elapsedDays)),
            ...campo('Saldo Prazo:', prazo === undefined ? VAZIO : dias(prazo.remainingDays)),
          ],
          [
            ...campo('Responsável Técnico:', project.technicalManager ?? VAZIO),
            ...campo('Cliente:', project.client ?? VAZIO),
          ],
          [...campo('Data:', dataCurta), ...campo('Dia da Semana:', diaDaSemana)],
          [...campo('Documento:', identificacao), ...campo('Endereço:', project.address ?? VAZIO)],
          [
            { text: 'Observação:', style: 'rotulo', fillColor: CINZA },
            {
              text: report.notes ?? 'Sem ocorrências registradas.',
              italics: report.notes === undefined,
              colSpan: 3,
            },
            ...vazias(2),
          ],
        ],
      },
      margin: [0, 0, 0, 10],
    },

    tabela(
      'Turno / Tempo',
      ['Turno', 'Horário', 'Tempo', 'Praticável'],
      ['auto', 'auto', '*', 'auto'],
      reportedShifts(report.shifts).map((turno) => [
        REPORT_SHIFT_LABEL[turno.shift],
        horario(turno),
        turno.condition === undefined ? VAZIO : WEATHER_CONDITION_LABEL[turno.condition],
        turno.workable ? 'Sim' : 'Não',
      ]),
      'Nenhum turno relatado neste dia.',
    ),

    tabela(
      'Tarefas Realizadas',
      ['Descrição', 'Qtd. Medida', 'Unidade', 'Observações'],
      ['*', 'auto', 'auto', 190],
      [
        ...fronts.map((linha) => [
          linha.description,
          { text: quantidadeDaFrente(linha), alignment: 'right' as const },
          linha.unitName ?? VAZIO,
          describeWorkers(linha),
        ]),
        // A avulsa conta o dia e não entra em apuração (RN-213); desde a Fase 20 ela
        // imprime quem fez, quanto e por quanto tempo, como texto do documento.
        ...report.tasks.map((tarefa) => [
          tarefa.description,
          {
            text:
              tarefa.quantity === undefined
                ? VAZIO
                : tarefa.quantity.toLocaleString('pt-BR', { maximumFractionDigits: 3 }),
            alignment: 'right' as const,
          },
          tarefa.unit ?? VAZIO,
          describeTaskDetails(tarefa) || VAZIO,
        ]),
      ],
      'Nenhuma tarefa registrada neste dia.',
    ),

    // A qualidade sai das verificações do dia (ADR-128): frente, serviço declarado, a
    // ficha respondida e a NC com o número dela — é ele que o FORM.30 acompanha.
    tabela(
      'Verificação de Qualidade',
      ['Frente', 'Serviço', 'Checklist', 'Aprov.', 'Reprov.', 'Não conformidades'],
      ['*', 'auto', 'auto', 'auto', 'auto', 150],
      quality.map((linha) => [
        linha.description,
        linha.serviceName,
        linha.checklistName ?? 'sem checklist',
        {
          text: linha.checklistName === undefined ? VAZIO : String(linha.approved),
          alignment: 'right' as const,
        },
        {
          text: linha.checklistName === undefined ? VAZIO : String(linha.rejected),
          alignment: 'right' as const,
        },
        describeNonConformities(linha),
      ]),
      'Nenhuma verificação de qualidade neste dia.',
    ),

    tabela(
      'Equipe Envolvida',
      ['Descrição', 'Qtd. Utilizada'],
      ['*', 'auto'],
      crewParaPapel(crew).map((linha) => [
        linha.roleName,
        { text: String(linha.quantity), alignment: 'right' as const },
      ]),
      'Nenhum efetivo registrado neste dia.',
    ),

    tabela(
      'Equipamento Utilizado',
      ['Código', 'Descrição', 'Status', 'Qtd. Utilizada'],
      ['auto', '*', 'auto', 'auto'],
      report.equipment.map((linha: EquipmentLine) => [
        linha.code ?? VAZIO,
        linha.description,
        EQUIPMENT_STATUS_LABEL[linha.status],
        { text: String(linha.quantity), alignment: 'right' as const },
      ]),
      'Nenhum equipamento registrado neste dia.',
    ),

    // A linha de assinatura é impressa, e não assinada digitalmente — está fora do escopo
    // da fase, e o papel diz o que é sem prometer o que não é.
    {
      columns: [
        { stack: [{ text: '_'.repeat(38) }, { text: 'Responsável técnico', fontSize: 8 }] },
        { stack: [{ text: '_'.repeat(38) }, { text: 'Contratante / fiscalização', fontSize: 8 }] },
      ],
      columnGap: 24,
      margin: [0, 16, 0, 0],
    },
  ];

  if (photos.length > 0) {
    /*
     * A segunda página, na grade do formulário: duas fotos por linha, cada uma na sua
     * moldura, com a legenda em caixa alta embaixo — é ela que diz o que se está vendo a
     * quem não esteve no canteiro (RN-214). O par ímpar termina com a célula vazia, e não
     * com uma foto esticada na largura da página.
     */
    conteudo.push({ text: VAZIO, pageBreak: 'before', style: 'quebra' });
    conteudo.push({
      table: {
        headerRows: 1,
        widths: ['50%', '50%'],
        body: [
          barra('RELATÓRIO FOTOGRÁFICO', 2),
          ...emPares(photos).map((par) => [
            ...par.map((foto): Cell => ({
              stack: [
                { image: foto.dataUrl, width: 230 },
                {
                  text: foto.caption.toUpperCase(),
                  style: 'legenda',
                  alignment: 'center' as const,
                },
              ],
              alignment: 'center' as const,
            })),
            ...vazias(2 - par.length),
          ]),
        ],
      },
    });
  }

  return {
    pageSize: 'A4',
    pageMargins: [40, 56, 40, 48],
    info: { title: `${identificacao} — ${project.name}` },
    defaultStyle: { fontSize: 8 },
    styles: {
      /** A faixa do topo: o nome do documento, como no formulário da obra. */
      documento: { fontSize: 14, bold: true, margin: [0, 4, 0, 4] },
      /** A barra de cada seção — cinza, centralizada, sobre a grade dela. */
      barra: { fontSize: 9, bold: true, fillColor: '#e9e9e9', margin: [0, 2, 0, 2] },
      rotulo: { bold: true, alignment: 'right' },
      th: { bold: true, fontSize: 7.5 },
      legenda: { fontSize: 7.5, bold: true, margin: [0, 4, 0, 2] },
      quebra: { fontSize: 1 },
    },
    // O cabeçalho se repete entre páginas, e é justamente por isso que a fase escolheu
    // `pdfmake`: quem recebe a segunda página solta precisa saber de que RDO ela é.
    header: (pagina) =>
      pagina === 1
        ? undefined
        : {
            text: `${identificacao} — ${project.name} — ${dateLabel}`,
            fontSize: 8,
            margin: [40, 24, 40, 0],
          },
    footer: (pagina, total) => ({
      text: `${identificacao} · ${dateLabel} · página ${String(pagina)} de ${String(total)}`,
      fontSize: 7,
      alignment: 'center',
      margin: [40, 16, 40, 0],
    }),
    content: conteudo,
  };
}

/**
 * Só o corrigido vai ao papel (RN-212), e a função sem ninguém não vai.
 *
 * Corrigir **para zero** é correção — "hoje não tinha pedreiro" é uma frase —, e ela vale
 * na tela, onde a diferença entre o contado e o corrigido é informação. No papel, uma
 * linha `PEDREIRO 0` diria ao contratante que havia uma função com ninguém nela.
 */
function crewParaPapel(crew: readonly CrewLine[]): readonly CrewLine[] {
  return crew.filter((linha) => linha.quantity > 0);
}

function emPares<T>(itens: readonly T[]): readonly (readonly T[])[] {
  const pares: T[][] = [];
  for (let i = 0; i < itens.length; i += 2) pares.push(itens.slice(i, i + 2));
  return pares;
}

/**
 * O nome do arquivo — `RDO-0142-rev2-2026-09-10-VIE.pdf`.
 *
 * Padronizado porque ele vai virar anexo de e-mail e linha de pasta, e é aí que o nome
 * passa a ser o índice: o número vem **preenchido com zeros** para a pasta ordenar por
 * ele, e a data em `AAAA-MM-DD` pela mesma razão. O código da obra fecha, porque duas
 * obras na mesma caixa de entrada geram RDO nº 1 no mesmo dia.
 *
 * A revisão só aparece quando existe mais de uma: `rev1` em todo arquivo seria ruído em
 * 99% deles, e a ausência já diz "primeira emissão".
 */
export function dailyReportFileName(report: DailyReport, project: Project): string {
  const numero = String(report.number ?? 0).padStart(4, '0');
  const revisao = report.revision > 1 ? `-rev${String(report.revision)}` : '';
  const obra = project.code
    .normalize('NFD')
    .replaceAll(/[̀-ͯ]/gu, '')
    .replaceAll(/[^A-Za-z0-9]+/gu, '-')
    .replaceAll(/^-|-$/gu, '')
    .toUpperCase();
  return `RDO-${numero}${revisao}-${report.date}${obra === '' ? '' : `-${obra}`}.pdf`;
}

/**
 * Carrega o `pdfmake` e baixa o arquivo.
 *
 * O `import()` é dinâmico e não é preferência de estilo: a biblioteca e as fontes somam
 * mais de um megabyte, e a `/conferencia` é aberta todo dia por gente que não vai emitir
 * RDO nenhum. Estático, toda a obra pagaria o download por uma ação que a maioria faz uma
 * vez por dia — e alguns, nunca.
 *
 * O `await` no `download` é o que faz a falha chegar a quem chamou. Na 0.3 a montagem do
 * arquivo acontece **dentro** da promessa que ele devolve; sem esperá-la, um erro de lá
 * virava rejeição solta — nenhum arquivo, nenhum aviso, e o botão voltava a "Baixar o
 * PDF" antes de o documento sequer existir.
 */
export async function emitirPdfDoRelatorio(entrada: DailyReportDocumentInput): Promise<void> {
  const [{ default: pdfMake }, { default: vfs }] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);

  pdfMake.addVirtualFileSystem(vfs);
  await pdfMake
    .createPdf(buildDailyReportDocument(entrada))
    .download(dailyReportFileName(entrada.report, entrada.project));
}
